import { useState, useCallback, useEffect } from 'react';
import { useOutlookEmail } from './useOutlookEmail';

/**
 * Hook for managing email threads
 * Groups messages by conversation ID to create thread view
 */
export const useOutlookThreads = (options = {}) => {
  const { folderId, connectionId, autoFetch = true } = options;
  
  const [threads, setThreads] = useState([]);
  const [selectedThread, setSelectedThread] = useState(null);
  const [threadMessages, setThreadMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingThread, setLoadingThread] = useState(false);
  const [error, setError] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(0);

  const { getMessages, getMessage } = useOutlookEmail();

  // Fetch threads (messages grouped by conversation)
  const fetchThreads = useCallback(async (reset = false) => {
    if (loading) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const skip = reset ? 0 : page * 50;
      const result = await getMessages({
        folderId,
        connectionId,
        top: 50,
        skip,
      });

      // Group messages by threadId/conversationId
      const messagesByThread = {};
      result.messages.forEach(msg => {
        const threadId = msg.threadId || msg.id;
        if (!messagesByThread[threadId]) {
          messagesByThread[threadId] = {
            id: threadId,
            messages: [],
            latest: null,
            hasUnread: false,
            subject: '',
            participants: new Set(),
          };
        }
        messagesByThread[threadId].messages.push(msg);
        if (msg.unread) {
          messagesByThread[threadId].hasUnread = true;
        }
        messagesByThread[threadId].participants.add(msg.sender.email);
      });

      // Process threads
      const processedThreads = Object.values(messagesByThread).map(thread => {
        // Sort messages by date, get latest
        thread.messages.sort((a, b) => 
          new Date(b.receivedOn) - new Date(a.receivedOn)
        );
        thread.latest = thread.messages[0];
        thread.subject = thread.latest?.subject || '';
        thread.participants = Array.from(thread.participants);
        thread.messageCount = thread.messages.length;
        return thread;
      });

      // Sort threads by latest message date
      processedThreads.sort((a, b) => 
        new Date(b.latest?.receivedOn) - new Date(a.latest?.receivedOn)
      );

      if (reset) {
        setThreads(processedThreads);
        setPage(1);
      } else {
        setThreads(prev => [...prev, ...processedThreads]);
        setPage(prev => prev + 1);
      }

      setHasMore(!!result.nextPageToken || result.messages.length === 50);
    } catch (err) {
      setError(err.message || 'Failed to fetch threads');
    } finally {
      setLoading(false);
    }
  }, [loading, page, folderId, connectionId, getMessages]);

  // Load more threads
  const loadMore = useCallback(() => {
    if (!loading && hasMore) {
      fetchThreads(false);
    }
  }, [loading, hasMore, fetchThreads]);

  // Refresh threads
  const refresh = useCallback(() => {
    setPage(0);
    fetchThreads(true);
  }, [fetchThreads]);

  // Select a thread and load its messages with full body content
  const selectThread = useCallback(async (threadId) => {
    if (!threadId) {
      setSelectedThread(null);
      setThreadMessages([]);
      return;
    }

    setLoadingThread(true);
    setSelectedThread(threadId);

    try {
      // Find thread in current list
      const thread = threads.find(t => t.id === threadId);
      if (thread) {
        // Fetch full message details including body for each message
        const messagesWithBody = await Promise.all(
          thread.messages.map(async (msg) => {
            try {
              const fullMessage = await getMessage(msg.id);
              return fullMessage;
            } catch (err) {
              // If fetching fails, return original message
              console.error(`Failed to fetch message ${msg.id}:`, err);
              return msg;
            }
          })
        );

        // Sort messages chronologically for display
        const sortedMessages = messagesWithBody.sort((a, b) =>
          new Date(a.receivedOn) - new Date(b.receivedOn)
        );
        setThreadMessages(sortedMessages);
      }
    } catch (err) {
      setError(err.message || 'Failed to load thread');
    } finally {
      setLoadingThread(false);
    }
  }, [threads, getMessage]);

  // Close thread
  const closeThread = useCallback(() => {
    setSelectedThread(null);
    setThreadMessages([]);
  }, []);

  // Auto-fetch on mount
  useEffect(() => {
    if (autoFetch) {
      fetchThreads(true);
    }
  }, [folderId, connectionId]); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    threads,
    selectedThread,
    threadMessages,
    loading,
    loadingThread,
    error,
    hasMore,
    fetchThreads: () => fetchThreads(true),
    loadMore,
    refresh,
    selectThread,
    closeThread,
  };
};

export default useOutlookThreads;
